import { rgba } from 'polished'
import {
  Box,
  CSSObject,
  Flex,
  Image,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Spacer,
  Text,
  useDisclosure,
  useTheme,
} from '@ritualco/jutsu'
import { ChevronDownIcon, ChevronRightIcon } from '@chakra-ui/icons'
import { useContent } from 'lib/content/react'
import { forwardRef } from 'react'
import { Link } from 'lib/router'

import BuildingIcon from 'assets/Icons/DefaultBuilding.svg'

type Workspace = {
  workspaceId: string
  name: string
}

type Organization = {
  organizationId: string
  name: string
}

export type OrganizationMenuProps = {
  name: string
  iconUrl?: string
  workspaces: Workspace[]
  organizations: Organization[]
  currentWorkspaceId: string
  onWorkspaceChange: (workspaceId: string) => void
}

export const OrganizationMenu = ({
  name,
  iconUrl,
  workspaces,
  organizations = [],
  currentWorkspaceId,
  onWorkspaceChange,
}: OrganizationMenuProps) => {
  const theme = useTheme()
  const { formatMessage } = useContent()

  const orgMenuBtnStyles: CSSObject = {
    bgColor: 'grayscale.offWhite',
    border: 'none',
    borderRadius: 0,
    boxShadow: 'inset 0px -1px 0px #E0E0E2',
    color: 'grayscale.tertiary',
    px: 6,
    py: 4,
    width: '100%',
    height: '100%',
    borderRight: '1px solid',
    borderRightColor: 'grayscale.quinary',
    outline: 'revert',
    _hover: {
      bg: rgba(theme.colors.grayscale.quinary, 0.3),
    },
  }

  const currentWorkspace = workspaces.find(
    ({ workspaceId }) => workspaceId === currentWorkspaceId
  )

  const listItemStyles: CSSObject = {
    border: 'none',
    borderRadius: 0,
    fontWeight: 'normal',
    height: 'unset',
    p: 3,
    _hover: {
      backgroundColor: `${rgba(theme.colors.primary.normal, 0.15)} !important`,
      color: 'primary.darkest',
    },
  }

  return (
    <Menu
      matchWidth
      placement="bottom-end"
      offset={[18, -8]}
      closeOnSelect={false}
    >
      {({ onClose }) => (
        <>
          <MenuButton sx={orgMenuBtnStyles}>
            <Flex align="center">
              <Flex direction="column" align="flex-start">
                <Text
                  noOfLines={1}
                  color="grayscale.primary"
                  fontSize="medium"
                  fontWeight="medium"
                >
                  {name}
                </Text>
                {currentWorkspace && (
                  <Text
                    noOfLines={1}
                    color="grayscale.secondary"
                    fontSize="small"
                    fontWeight="medium"
                  >
                    {currentWorkspace.name}
                  </Text>
                )}
              </Flex>
              <Spacer />
              <ChevronDownIcon ml={4} boxSize={24} />
            </Flex>
          </MenuButton>
          <MenuList zIndex="dropdown">
            <Flex px={3} py={2} align="center">
              <Image
                borderRadius="full"
                boxSize="48px"
                fit="cover"
                src={iconUrl || BuildingIcon}
                alt={name}
                mr={2}
              />
              <Text fontSize="small" fontWeight="medium">
                {name}
              </Text>
            </Flex>
            {workspaces.length > 1 && (
              <MenuItem
                as={SubMenu}
                workspaces={workspaces}
                onWorkspaceChange={onWorkspaceChange}
                onCloseMenu={onClose}
              />
            )}
            {organizations.length > 1 && (
              <Link to="/workspace-selector">
                <MenuItem sx={listItemStyles}>
                  {formatMessage({ id: 'pmportal.global.seeAllWorkspaces' })}
                </MenuItem>
              </Link>
            )}
          </MenuList>
        </>
      )}
    </Menu>
  )
}

// https://twitter.com/thesegunadebayo/status/1257408344169558016/photo/1
type SubMenuProps = {
  onCloseMenu: Function
} & Pick<OrganizationMenuProps, 'workspaces' | 'onWorkspaceChange'>
const SubMenu = forwardRef<HTMLButtonElement, SubMenuProps>((props, ref) => {
  const { formatMessage } = useContent()
  const theme = useTheme()
  const listItemStyles: CSSObject = {
    border: 'none',
    borderRadius: 0,
    fontWeight: 'normal',
    height: 'unset',
    p: `${theme.space[3]} !important`,
    _expanded: {
      backgroundColor: `${rgba(theme.colors.primary.normal, 0.15)} !important`,
      color: 'primary.darkest',
    },
  }
  const { workspaces, onWorkspaceChange, onCloseMenu, ...rest } = props
  const { isOpen, onOpen, onClose } = useDisclosure()

  return (
    <Menu
      placement="right-start"
      offset={[-9, 0]}
      isOpen={isOpen}
      onClose={onClose}
    >
      <Box onClick={onOpen} onMouseEnter={onOpen} onMouseLeave={onClose}>
        <MenuButton ref={ref} sx={listItemStyles} {...rest}>
          <Flex align="center">
            <Text textAlign="initial">
              {formatMessage({ id: 'pmportal.global.buildingList' })}
            </Text>
            <Spacer />
            <ChevronRightIcon boxSize={18} />
          </Flex>
        </MenuButton>
        <MenuList zIndex="dropdown">
          {workspaces.map(({ workspaceId, name }) => (
            <MenuItem
              key={workspaceId}
              p={3}
              onClick={() => {
                onWorkspaceChange(workspaceId)
                onCloseMenu()
              }}
            >
              {name}
            </MenuItem>
          ))}
        </MenuList>
      </Box>
    </Menu>
  )
})
